// src/modules/Audit/AuditDetails.js
// some imports and styles not working or missing i.e Ficheck and Motion
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FiCheck, FiX, FiAlertCircle } from 'react-icons/fi';
import { Pie } from 'react-chartjs-2';
import { jsPDF } from "jspdf";
import 'jspdf-autotable';
import BackButton from '../../components/BackButton'; // Adjust the import path 

const AuditDetails = ({ auditId }) => {
  const [auditData, setAuditData] = useState(null);
  const [activeSection, setActiveSection] = useState('frontOfHouse');
  const [overallScore, setOverallScore] = useState(0);

  useEffect(() => {
    // Fetch audit data here
    // For now, using mock data
    const mockAuditData = {
      id: auditId,
      date: '2023-06-01',
      branch: 'Branch A',
      sections: {
        frontOfHouse: {
          items: [
            { name: "Parking lot/sidewalks are clean", score: 2, maxScore: 2 },
            { name: "Exterior building is clean & well maintained", score: 1, maxScore: 2 },
            // ... other items
          ]
        },
        foodSafety: {
          items: [
            { name: "Required hand washing procedures are followed", score: 2, maxScore: 2 },
            { name: "Health Department inspections are available", score: 2, maxScore: 2 },
            // ... other items
          ]
        },
        // ... other sections
      }
    };
    setAuditData(mockAuditData);
    calculateOverallScore(mockAuditData);
  }, [auditId]);

  const calculateOverallScore = (data) => {
    let totalScore = 0;
    let totalMaxScore = 0;
    Object.values(data.sections).forEach(section => {
      section.items.forEach(item => {
        totalScore += item.score;
        totalMaxScore += item.maxScore;
      });
    });
    setOverallScore((totalScore / totalMaxScore) * 100);
  };

  const handleScoreChange = (sectionKey, itemIndex, newScore) => {
    const updatedAuditData = { ...auditData };
    updatedAuditData.sections[sectionKey].items[itemIndex].score = newScore;
    setAuditData(updatedAuditData);
    calculateOverallScore(updatedAuditData);
  };

  const getScoreColor = (score, maxScore) => {
    const percentage = (score / maxScore) * 100;
    if (percentage >= 90) return 'text-green-500';
    if (percentage >= 80) return 'text-yellow-500';
    return 'text-red-500';
  };

  const generatePDF = () => {
    const doc = new jsPDF();
    doc.setFontSize(18);
    doc.text('Audit Report', 14, 22);
    doc.setFontSize(12);
    doc.text(`Audit ID: ${auditData.id}`, 14, 32);
    doc.text(`Date: ${auditData.date}`, 14, 40);
    doc.text(`Branch: ${auditData.branch}`, 14, 48);
    doc.text(`Overall Score: ${overallScore.toFixed(1)}%`, 14, 56);

    let yOffset = 70;
    Object.entries(auditData.sections).forEach(([sectionName, sectionData]) => {
      doc.setFontSize(14);
      doc.text(sectionName, 14, yOffset);
      yOffset += 10;

      const tableData = sectionData.items.map(item => [item.name, item.score, item.maxScore]);
      doc.autoTable({
        startY: yOffset,
        head: [['Item', 'Score', 'Max Score']],
        body: tableData,
      });
      yOffset = doc.lastAutoTable.finalY + 10;
    });

    doc.save(`audit-report-${auditData.id}.pdf`);
  };

  if (!auditData) return <div>Loading...</div>;

  return (
    <div className="bg-gray-100 min-h-screen p-8">
      <BackButton />
      <h1 className="text-3xl font-bold mb-6">Audit Details</h1>
      <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h2 className="text-2xl font-semibold">Audit #{auditData.id}</h2>
            <p className="text-gray-600">Date: {auditData.date}</p>
            <p className="text-gray-600">Branch: {auditData.branch}</p>
          </div>
          <div className="text-right">
            <p className="text-4xl font-bold text-blue-500">{overallScore.toFixed(1)}%</p>
            <p className="text-gray-600">Overall Score</p>
          </div>
        </div>
        <button
          onClick={generatePDF}
          className="bg-blue-500 hover:bg-blue-600 text-white font-bold py-2 px-4 rounded"
        >
          Generate PDF Report
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
        {Object.keys(auditData.sections).map((sectionKey) => (
          <button
            key={sectionKey}
            onClick={() => setActiveSection(sectionKey)}
            className={`p-4 rounded-lg shadow transition-colors ${
              activeSection === sectionKey ? 'bg-blue-500 text-white' : 'bg-white text-gray-800'
            }`}
          >
            {sectionKey.replace(/([A-Z])/g, ' $1').trim()}
          </button>
        ))}
      </div>

      <div className="bg-white rounded-lg shadow-lg p-6">
        <h2 className="text-2xl font-semibold mb-4">
          {activeSection.replace(/([A-Z])/g, ' $1').trim()}
        </h2>
        {auditData.sections[activeSection].items.map((item, index) => (
          <div key={index} className="mb-4 p-4 bg-gray-50 rounded-lg">
            <div className="flex justify-between items-center">
              <span className="font-medium">{item.name}</span>
              <div className="flex items-center space-x-2">
                {[0, 1, 2].map((score) => (
                  <button
                    key={score}
                    onClick={() => handleScoreChange(activeSection, index, score)}
                    className={`w-8 h-8 rounded-full focus:outline-none transition-colors duration-200 ${
                      item.score === score ? 'bg-blue-500 text-white' : 'bg-gray-300 text-gray-800'
                    }`}
                  >
                    {score}
                  </button>
                ))}
                <span className={`font-semibold ${getScoreColor(item.score, item.maxScore)}`}>
                  {item.score}/{item.maxScore}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default AuditDetails;